import React, { useEffect, useMemo, useState } from "react";
import { NewModal, NewCreatableSelectList, MainContentPaddingTypes } from "../..";
import { SelectFieldOption_Values, ListStyle_Types } from "../../../Definitions";
import { useRemoveUser } from "../../_Components";
import { AllUser_Data } from "../../../Services/Agrisys_GqlDataCaller/UserCalls/GQLCalls/userCalls";
import NewButton from "../../BaseComponents/Buttons/newButton";

interface UserDeleteModal_Props {
  isModalOpen: boolean;
  closeModal: () => void;
  userlist: AllUser_Data[];
  onUserDeleted?: () => void;
}

export default function DeleteUserModal({ isModalOpen, closeModal, userlist, onUserDeleted }: UserDeleteModal_Props) {
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>("");
  const [selectedUserGroupId, setSelectedUserGroupId] = useState<number | null>(null);

  useEffect(() => {
    if (isModalOpen) {
      setSelectedUserId(null);
      setSelectedDomain("");
      setSelectedUserGroupId(null);
    }
  }, [isModalOpen]);

  // #region | Select Options
  const selectedUser = userlist.find((u) => u.user_id === selectedUserId);

  // User Options
  const userOptions: SelectFieldOption_Values[] = userlist.slice().sort((a,b) => a.username.localeCompare(b.username)).map((user) => ({
    label: user.username,
    value: String(user.user_id),
  }));

  // Company Options
  const companyOptions: SelectFieldOption_Values[] = selectedUser?.user_companies?.slice().sort((a, b) => a.name.localeCompare(b.name)).map((company) => ({
    label: company.name + " | " + company.domain,
    value: company.domain,
  })) ?? [];

  // Company Options
  const userGroupOptions: SelectFieldOption_Values[] = selectedUser?.user_groups?.filter((g) => selectedUser.user_companies?.some((c) => c.domain === selectedDomain)).map((group) => ({
    label: group.name,
    value: String(group.user_group_id),
  })) ?? [];
  // #endregion

  const args = useMemo(() => ({
    username: selectedUser?.username ?? "",
    domain: selectedDomain,
    userGroupId: selectedUserGroupId,
  }), [selectedUser, selectedDomain, selectedUserGroupId]);

  const { loading, error, fetchData } = useRemoveUser({ args });

  // #region | Select handles
  const handleSelect = (e: SelectFieldOption_Values | undefined, selectListIndex: number) => {
    if (selectListIndex === 0) {
      setSelectedUserId(Number(e?.value));
      setSelectedDomain("");
      setSelectedUserGroupId(null);
      return;
    }
    if (selectListIndex === 1) {
      setSelectedDomain(e?.value ?? "");
      setSelectedUserGroupId(null); // reset group when domain changes
      return;
    }
    if (selectListIndex === 2) {
      setSelectedUserGroupId(Number(e?.value))
    }
  }
  // #endregion
  
  if (!isModalOpen) return null;

  return (
    <NewModal
      modalState={isModalOpen}
      header={"delete user"}
      predefinedButtons={{
        onClose: closeModal
      }}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary
      }}
      mainContent={
        <NewCreatableSelectList
          placeholders={["", "", ""]}
          headers={["user", "domain", "group"]}
          options={[userOptions, companyOptions, userGroupOptions]}
          isClearable
          isSearchable
          onChanges={[userOptions, companyOptions, userGroupOptions].map((_, i) => (e) => handleSelect(e, i) )}
          isDisabled={[false, !selectedUserId, !selectedDomain]}
          listOverrides={{
            listStyle: ListStyle_Types.vertical,
            listGap: "10px"
          }}
        />              
      }
      footerContent={
        <NewButton
          onClick={async () => {
            const result = await fetchData();
            if (result?.success) {if (onUserDeleted) onUserDeleted(); closeModal()}
          }}
          content={"delete"}
        />
      }
      footerMessage={error ?? ""}
    />
  );
}
